import {
  localDateTimeInputToUtcIso
} from '@smarttransit/common'
import {
  addParamsToLocation,
  ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS,
  getAgentTransactionLogTypeOptions,
  mapAgentTransactionForDisplay,
  overlayPendingOutgoingGift
} from '@smarttransit/common-client'
import { addAlert, isAuthorized, isOnlyInvestor as isOnlyInvestorFn } from '../../../../../utilities/helpers'
import { getAgent, searchAgentGifts } from '../../../../../services/agents-service'
import {
  countAgentTransactionLogs,
  searchAgentTransactionLogs
} from '../../../../../services/agent-transaction-logs-service'

export default {
  name: 'agents-agent-transactions',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      currentAgent: null,
      currentAgentError: null,
      countryCurrencies: {},
      searchPhone: '',
      selectedLogTypes: [],
      dateFrom: null,
      dateTo: null,
      logTypes: getAgentTransactionLogTypeOptions(ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS),
      transactions: [],
      totalTransactions: 0,
      detailsDialogOpen: false,
      totalsDialogOpen: false,
      selectedTransaction: null,
      lastParams: {},
      componentMounted: false,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateUpdated'
      },
      headers: [
        { text: 'Type', align: 'left', sortable: true, value: 'logType' },
        { text: 'Amount', align: 'left', sortable: true, value: 'amount' },
        { text: 'Profit', align: 'left', sortable: false },
        { text: 'Phone', align: 'left', sortable: false },
        { text: 'Before', align: 'left', sortable: true, value: 'profileBalanceBefore' },
        { text: 'After', align: 'left', sortable: true, value: 'profileBalanceAfter' },
        { text: 'Status', align: 'left', sortable: true, value: 'logStatus' },
        { text: 'Updated', align: 'left', sortable: true, value: 'dateUpdated' }
      ]
    }
  },
  computed: {
    isAdmin () {
      return isAuthorized(this.$props.signedInUser, 'admin')
    },
    isOnlyInvestor () {
      return isOnlyInvestorFn(this.$props.signedInUser)
    },
    agentId () {
      return this.$route.params.agentId
    },
    agentNameLabel () {
      if (!this.currentAgent || !this.currentAgent.stUser) {
        return ''
      }
      return [this.currentAgent.stUser.firstName, this.currentAgent.stUser.lastName].filter(Boolean).join(' ')
    },
    currencyPrefix () {
      if (!this.currentAgent || !this.countryCurrencies) {
        return ''
      }
      const currency = this.countryCurrencies[this.currentAgent.countryId]
      return (currency && currency.currencySymbol) || ''
    },
    canViewPeriodTotals () {
      return !!(this.isAdmin && !this.isOnlyInvestor && this.currentAgent && this.currentAgent.id)
    }
  },
  watch: {
    '$route.params.agentId' () {
      this.componentMounted = false
      this.transactions = []
      this.currentAgent = null
      this.currentAgentError = null
      this.loadPage().then(() => {
        this.componentMounted = true
      })
    },
    pagination (updatedPagination) {
      this.lastParams = addParamsToLocation(updatedPagination, this.$route, this.lastParams)
    }
  },
  mounted () {
    this.loadFromQueryParams()
    this.$store.getters.getCountryCurrencies.then((results) => {
      this.countryCurrencies = results || {}
    }).catch((error) => {
      addAlert({ message: error, type: 'error' })
    })
    this.loadPage().then(() => {
      this.componentMounted = true
    })
  },
  methods: {
    loadFromQueryParams () {
      if (this.$route.query.descending) {
        this.pagination.descending = this.$route.query.descending === 'true' || this.$route.query.descending === true
      }
      if (this.$route.query.page) {
        this.pagination.page = Number(this.$route.query.page) || 1
      }
      if (this.$route.query.rowsPerPage) {
        this.pagination.rowsPerPage = Number(this.$route.query.rowsPerPage) || 10
      }
      if (this.$route.query.sortBy) {
        this.pagination.sortBy = this.$route.query.sortBy
      }
      if (this.$route.query.searchPhone) {
        this.searchPhone = this.$route.query.searchPhone
      }
      if (this.$route.query.dateFrom) {
        this.dateFrom = this.$route.query.dateFrom
      }
      if (this.$route.query.dateTo) {
        this.dateTo = this.$route.query.dateTo
      }
      if (this.$route.query.selectedLogTypes) {
        const selectedLogTypes = Array.isArray(this.$route.query.selectedLogTypes)
          ? this.$route.query.selectedLogTypes
          : [this.$route.query.selectedLogTypes]
        this.selectedLogTypes = selectedLogTypes.filter(Boolean)
      }
    },
    async loadPage () {
      if (!this.isAdmin || this.isOnlyInvestor) {
        this.currentAgentError = 'The current user does not have permission to view agent transactions'
        return
      }
      try {
        this.currentAgent = await getAgent({ id: this.agentId, includeArchived: true })
        await this.searchTransactions()
      } catch (error) {
        this.currentAgentError = (error && error.message) || 'Could not load agent'
        addAlert({ message: error, type: 'error' })
      }
    },
    submitSearch () {
      this.lastParams = addParamsToLocation({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        searchPhone: this.searchPhone,
        selectedLogTypes: this.selectedLogTypes
      }, this.$route, this.lastParams)
      this.pagination.page = 1
      if (this.componentMounted) {
        this.searchTransactions()
      }
    },
    buildSearchOptions () {
      const { sortBy, descending, page, rowsPerPage } = this.pagination
      return {
        agentProfileId: this.agentId,
        dateFrom: localDateTimeInputToUtcIso(this.dateFrom),
        dateTo: localDateTimeInputToUtcIso(this.dateTo),
        searchPhone: this.searchPhone,
        logTypes: this.selectedLogTypes,
        orderBy: sortBy || 'dateUpdated',
        sortOrder: descending ? 'DESC' : 'ASC',
        page: page,
        limit: rowsPerPage
      }
    },
    async searchTransactions () {
      if (!this.isAdmin || this.isOnlyInvestor || !this.agentId) {
        return
      }
      this.apiInProgress = true
      try {
        const options = this.buildSearchOptions()
        const [transactions, total, gifts] = await Promise.all([
          searchAgentTransactionLogs(options),
          countAgentTransactionLogs(options),
          searchAgentGifts({ agentProfileId: this.agentId }).catch((error) => {
            console.error(error)
            return []
          })
        ])
        const currencyPrefix = this.currencyPrefix
        this.transactions = (transactions || []).map((transaction) => {
          return overlayPendingOutgoingGift(mapAgentTransactionForDisplay(transaction, {
            currencyPrefix: currencyPrefix,
            logTypeLabels: ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS
          }), gifts || [])
        })
        this.totalTransactions = total
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    onPagination () {
      if (this.componentMounted) {
        this.searchTransactions()
      }
    },
    openTransactionDetails (item) {
      this.selectedTransaction = item
      this.detailsDialogOpen = true
    }
  }
}
